import { ConnectionsMenu } from "@/components/MessageInput/components/ConnectionsMenu";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  getViewportDocumentCollisionBoundary,
  VIEWPORT_POPOVER_COLLISION_PADDING,
} from "@/constants/viewportPopoverCollision";
import { allowedExtensions } from "@/hooks/useFileRecordUpload";
import { cn } from "@/utils";
import analytics from "@/utils/analytics";
import { IntegrationConnection } from "@cohesive/api-client";
import {
  Entity,
  getEntityData,
  getEntityName,
  getEntityType,
  INSERT_CONNECTION_COMMAND,
  IntegrationIcon,
} from "@cohesive/ui-components";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useRect } from "@radix-ui/react-use-rect";
import { AtSign, Paperclip, X } from "lucide-react";
import toast from "react-hot-toast";

interface AddMenuButtonProps {
  iconClassName?: string;
  /** Merged onto the @ trigger `Button` (e.g. compact canvas toolbar). */
  triggerButtonClassName?: string;

  onAttachFile?: (files: File[]) => void;
  filteredConnections: IntegrationConnection[];
  connectionsEnabled: boolean;
  fileUploadEnabled: boolean;
  connectionsFilter: string;
  setConnectionsFilter: (filter: string) => void;
  containerRect: ReturnType<typeof useRect>;
  addMenuOpen: boolean;
  setAddMenuOpen: (open: boolean) => void;
  showConnectionsInAddMenu: boolean;
  setShowConnectionsInAddMenu: (show: boolean) => void;
  activeConnection: IntegrationConnection | null;
  setActiveConnection: (connection: IntegrationConnection | null) => void;
  setMentionMode: (mode: "@" | "/" | null) => void;
  focusTextarea: () => void;
}

export const AddMenuButton: React.FC<AddMenuButtonProps> = ({
  iconClassName,
  triggerButtonClassName,

  onAttachFile,
  filteredConnections,
  connectionsEnabled,
  fileUploadEnabled,
  connectionsFilter,
  setConnectionsFilter,
  containerRect = {
    left: 0,
    right: 0,
  },
  addMenuOpen,
  setAddMenuOpen,
  showConnectionsInAddMenu,
  setShowConnectionsInAddMenu,
  activeConnection,
  setActiveConnection,
  setMentionMode,
  focusTextarea,
}) => {
  const [editor] = useLexicalComposerContext();

  const handleRecordSelect = (entity: Entity) => {
    try {
      const entityData = getEntityData(entity);
      // Generic entities fix `platform` to "generic" and carry the real
      // source platform in `data.platform_name` — prefer that so the mention
      // still identifies e.g. Zoom/Gmail rather than just "generic".
      const platform = entityData?.platform_name || entity.platform;
      const type = getEntityType(entity);
      const id = entityData?.id?.toString() || "";
      const name = getEntityName(entity);

      editor.dispatchCommand(INSERT_CONNECTION_COMMAND, {
        platform,
        type,
        identifier: id,
        title: name,
      });
      setActiveConnection(null);
      setShowConnectionsInAddMenu(false);
      setAddMenuOpen(false);
      setMentionMode(null);
      analytics.trackIntegrationConnectionRecordSelected({
        entity: {
          platform,
          type,
          id,
          name,
        },
      });
      setTimeout(() => {
        focusTextarea();
      }, 0);
    } catch (error) {
      console.error("Failed to create connection from entity:", error);
      toast.error("Invalid entity format");
    }
  };

  const handleAttach = () => {
    // Create a temporary file input to trigger file selection
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.multiple = true;
    fileInput.style.display = "none";

    // Set accepted file extensions
    fileInput.accept = allowedExtensions.map((ext) => `.${ext}`).join(",");

    fileInput.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files) {
        onAttachFile?.(Array.from(target.files));
      }
      document.body.removeChild(fileInput);
    };

    document.body.appendChild(fileInput);
    fileInput.click();
  };

  return (
    <div className="z-9 order-2">
      <Popover
        open={addMenuOpen}
        onOpenChange={(open) => {
          setAddMenuOpen(open);
          if (!open) {
            focusTextarea();
          }
        }}
      >
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className={cn(
                  "text-muted-foreground hover:text-foreground transition-colors",
                  triggerButtonClassName,
                )}
              >
                <AtSign className={iconClassName} />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Add context '@'</TooltipContent>
        </Tooltip>
        <PopoverContent
          align="start"
          sideOffset={8}
          collisionBoundary={getViewportDocumentCollisionBoundary()}
          collisionPadding={VIEWPORT_POPOVER_COLLISION_PADDING}
          className={cn(
            "p-2 rounded-[14px] border transition-all duration-300",
            showConnectionsInAddMenu && activeConnection
              ? "compose-popover--wide"
              : "w-[384px] min-w-[384px]",
          )}
          style={
            showConnectionsInAddMenu && activeConnection && containerRect.width
              ? { width: Math.max(700, containerRect.width - 40) }
              : undefined
          }
          onCloseAutoFocus={(e) => {
            // Prevent popover from auto-focusing the trigger button on close.
            // This ensures focus returns to the textarea input instead.
            e.preventDefault();
          }}
        >
          {showConnectionsInAddMenu && activeConnection ? (
            <ConnectionsMenu
              activeConnection={activeConnection}
              setConnectionsMenuOpen={(open) => {
                setShowConnectionsInAddMenu(open);
                if (!open) {
                  setActiveConnection(null);
                  focusTextarea();
                }
              }}
              handleRecordSelect={handleRecordSelect}
            />
          ) : (
            <Command
              className="bg-transparent rounded-none overflow-hidden w-full"
              shouldFilter={false}
            >
              <CommandList className="max-h-[520px] overflow-y-auto scrollbar-thin scrollbar-thumb-neutral-300 dark:scrollbar-thumb-neutral-600 scrollbar-track-transparent">
                <CommandGroup>
                  {connectionsEnabled && (
                    <div
                      className={cn(
                        "px-2 [&_.border-b]:border-0 relative",
                        connectionsFilter ? "pb-0" : "pb-2",
                      )}
                    >
                      <CommandInput
                        placeholder="Search connectors..."
                        value={connectionsFilter}
                        onValueChange={setConnectionsFilter}
                        className="h-11"
                      />
                      {connectionsFilter && (
                        <button
                          onClick={() => setConnectionsFilter("")}
                          className="absolute right-5 top-1/2 -translate-y-1/2 p-1"
                        >
                          <X className="w-4 h-4 text-muted-foreground hover:text-foreground transition-colors" />
                        </button>
                      )}
                    </div>
                  )}
                  {!connectionsFilter &&
                    fileUploadEnabled &&
                    typeof onAttachFile === "function" && (
                      <CommandItem
                        value="add-file"
                        className="flex items-center gap-3 h-11 px-4"
                        onSelect={() => {
                          setAddMenuOpen(false);
                          handleAttach();
                        }}
                      >
                        <Paperclip className="w-5 h-5" />
                        <span>Add file</span>
                      </CommandItem>
                    )}
                </CommandGroup>
                {connectionsEnabled && (
                  <CommandGroup heading="Connectors">
                    {filteredConnections.map((conn) => (
                      <CommandItem
                        key={conn.id}
                        className="flex items-center gap-3 h-11 px-4"
                        onSelect={() => {
                          setShowConnectionsInAddMenu(true);
                          setActiveConnection(conn);
                          // Reset mention mode when navigating to entity selector
                          setMentionMode(null);
                        }}
                      >
                        <IntegrationIcon
                          platform={conn.integration_platform}
                          name={conn.integration_name}
                          imageURL={conn.integration_image_url}
                          className="w-5 h-5 rounded object-cover"
                        />
                        <span>{conn.integration_name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};
